import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ProfessionRuleInput, ProfessionRulePatch, TenantProfessionInput } from "@juro/shared";
import { tenantProfessionsApi } from "../api/tenantProfessionsApi.js";
import { toast } from "../components/Toast.js";

/** IssuerValuesForm がまとめて保存する差分（本体＋ルール値の更新・追加） */
export type IssuerMasterSaveInput = {
  profession: string;
  head?: TenantProfessionInput;
  updates: { ruleId: string; patch: ProfessionRulePatch }[];
  creates: ProfessionRuleInput[];
};

export function useIssuerMasterMutations(onSaved?: () => void) {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["tenant-professions"] });
    // 既定の資格者（holder_id）の変更で is_default が変わるため、
    // 全職能分の資格者名簿も無効化する
    qc.invalidateQueries({ queryKey: ["tenant-profession-holders"] });
  };

  const saveMut = useMutation({
    mutationFn: async (input: IssuerMasterSaveInput) => {
      if (input.head) await tenantProfessionsApi.updateHead(input.profession, input.head);
      for (const { ruleId, patch } of input.updates) {
        await tenantProfessionsApi.updateRule(input.profession, ruleId, patch);
      }
      for (const create of input.creates) {
        await tenantProfessionsApi.createRule(input.profession, create);
      }
    },
    onSuccess: () => {
      invalidate();
      toast("発行者情報を保存しました");
      onSaved?.();
    },
    onError: (e) => toast(e instanceof Error ? e.message : String(e), "error"),
  });

  const deleteRuleMut = useMutation({
    mutationFn: ({ profession, ruleId }: { profession: string; ruleId: string }) =>
      tenantProfessionsApi.deleteRule(profession, ruleId),
    onSuccess: () => {
      invalidate();
      toast("項目を削除しました");
    },
    onError: (e) => toast(e instanceof Error ? e.message : String(e), "error"),
  });

  return { saveMut, deleteRuleMut };
}
